import React from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Code
} from 'lucide-react';

interface ToolbarProps {
  editor: Editor | null;
}

export const Toolbar: React.FC<ToolbarProps> = ({ editor }) => {
  if (!editor) return null;

  // Force re-render when the editor selection/transaction changes so active states update
  const [, forceUpdate] = React.useState(0);
  React.useEffect(() => {
    if (!editor) return;
    const rerender = () => forceUpdate((v) => v + 1);
    editor.on('selectionUpdate', rerender);
    editor.on('transaction', rerender);
    editor.on('update', rerender);
    return () => {
      editor.off('selectionUpdate', rerender);
      editor.off('transaction', rerender);
      editor.off('update', rerender);
    };
  }, [editor]);

  const ToolButton: React.FC<{
    onClick: () => void;
    isActive?: boolean;
    children: React.ReactNode;
    title?: string;
  }> = ({ onClick, isActive, children, title }) => (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`
        p-2 rounded-md transition-colors
        ${isActive 
          ? 'bg-slate-100 text-slate-900' 
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }
      `}
    >
      {children}
    </button>
  );

  const Divider = () => (
    <div className="w-px h-6 bg-slate-200 mx-1" />
  );

  const setHeadingLevel = (level: 1 | 2) => {
    if (!editor) return;
    // If the exact level is active, toggle off to paragraph
    if (editor.isActive('heading', { level })) {
      editor.chain().focus().toggleHeading({ level }).run();
      return;
    }
    // If a different heading level is active, update the level
    if (editor.isActive('heading')) {
      editor.chain().focus().updateAttributes('heading', { level }).run();
      return;
    }
    // Otherwise, set heading of requested level
    editor.chain().focus().toggleHeading({ level }).run();
  };

  return (
    <div className="flex items-center gap-1 p-3 bg-white/90 backdrop-blur border-b border-slate-200 sticky top-[56px] z-20">
      {/* Text Formatting */}
      <ToolButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (⌘B)"
      >
        <Bold className="w-4 h-4" />
      </ToolButton>
      
      <ToolButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (⌘I)"
      >
        <Italic className="w-4 h-4" />
      </ToolButton>
      
      <ToolButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        title="Code (⌘E)"
      >
        <Code className="w-4 h-4" />
      </ToolButton>

      <Divider />

      {/* Headings */}
      <ToolButton
        onClick={() => setHeadingLevel(1)}
        isActive={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </ToolButton>
      
      <ToolButton
        onClick={() => setHeadingLevel(2)}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </ToolButton>

      <Divider />

      {/* Links & Images */}
      <ToolButton
        onClick={() => {
          if (editor.isActive('link')) {
            editor.chain().focus().unsetLink().run();
            return;
          }
          const previousUrl = editor.getAttributes('link').href as string | undefined;
          const url = window.prompt('Enter link URL', previousUrl || 'https://');
          if (!url) return;
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }}
        isActive={editor.isActive('link')}
        title={editor.isActive('link') ? 'Remove link' : 'Add link'}
      >
        <LinkIcon className="w-4 h-4" />
      </ToolButton>

      <ToolButton
        onClick={() => {
          const url = window.prompt('Enter image URL');
          if (!url) return;
          editor.chain().focus().setImage({ src: url }).run();
        }}
        title="Insert image"
      >
        <ImageIcon className="w-4 h-4" />
      </ToolButton>

      <Divider />

      {/* Lists */}
      <ToolButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </ToolButton>
      
      <ToolButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </ToolButton>

      <Divider />

      {/* History */}
      <ToolButton
        onClick={() => editor.chain().focus().undo().run()}
        title="Undo (⌘Z)"
      >
        <Undo className="w-4 h-4" />
      </ToolButton>
      
      <ToolButton
        onClick={() => editor.chain().focus().redo().run()}
        title="Redo (⌘⇧Z)"
      >
        <Redo className="w-4 h-4" />
      </ToolButton>
    </div>
  );
};